/**
 * Product lightbox (PhotoSwipe v4) for the main product gallery.
 *
 * Shows the whole gallery, not just the photos: photos as zoomable images, videos and a
 * drag-to-rotate 360 view as html slides, with a thumbnail row at the bottom. Used by product-slider
 * (assets/product.js) and product-slider-thumbnails (assets/product-thumbnails.js).
 *
 * Data comes from the markup: .product-single__media-zoom on photos, data-lightbox-embed /
 * data-lightbox-video on videos (snippets/product-media.liquid) and the frames of the 360
 * popup (snippets/product-360-popup.liquid).
 */
(function () {
  if (window.ProductLightbox) {
    return;
  }

  const escapeAttr = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');

  function buildItems(slides) {
    let items = [];
    slides.forEach((slide) => {
      let zoomLink = slide.querySelector('.product-single__media-zoom');
      let embed = slide.querySelector('[data-lightbox-embed]');
      let nativeVideo = slide.querySelector('[data-lightbox-video]');
      if (zoomLink) {
        items.push({
          src: zoomLink.getAttribute('href'),
          msrc: zoomLink.dataset.msrc,
          w: zoomLink.dataset.w,
          h: zoomLink.dataset.h,
          title: zoomLink.getAttribute('title'),
          slide: slide,
          thumb: zoomLink.dataset.msrc,
          kind: 'image'
        });
      } else if (embed && embed.dataset.lightboxEmbed) {
        items.push({
          html: '<div class="pswp-slide pswp-slide--video"><iframe class="pswp-slide__video" data-src="' + escapeAttr(embed.dataset.lightboxEmbed) + '" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen title="Video"></iframe></div>',
          slide: slide,
          thumb: embed.dataset.lightboxThumb,
          kind: 'video'
        });
      } else if (nativeVideo && nativeVideo.dataset.lightboxVideo) {
        items.push({
          html: '<div class="pswp-slide pswp-slide--video"><video class="pswp-slide__video" data-src="' + escapeAttr(nativeVideo.dataset.lightboxVideo) + '" controls playsinline></video></div>',
          slide: slide,
          thumb: nativeVideo.dataset.lightboxThumb,
          kind: 'video'
        });
      }
    });
    // 360: a copy of the 360 popup's own stage (frames, turntable ring, rotate arrows) from
    // snippets/product-360-popup.liquid, so both views look and rotate the same
    let stage = document.querySelector('.product-360-popup-modal .popup-viewer-stage');
    let badge360 = document.querySelector('.product-360-badge[data-popup-trigger-thumb]');
    if (stage && badge360) {
      let clone = stage.cloneNode(true);
      clone.querySelectorAll('img').forEach(img => img.removeAttribute('loading'));
      // The ring's gradient lives in the (hidden) popup; Chrome won't paint a gradient from a
      // display: none subtree, so the copy gets its own id
      clone.querySelectorAll('linearGradient[id]').forEach((gradient) => {
        let oldId = gradient.id;
        gradient.id = oldId + '-lightbox';
        clone.querySelectorAll('[stroke="url(#' + oldId + ')"]').forEach(el => el.setAttribute('stroke', 'url(#' + gradient.id + ')'));
      });
      clone.querySelectorAll('[data-popup-prev]').forEach(btn => { btn.removeAttribute('data-popup-prev'); btn.setAttribute('data-pswp-360-step', '-1'); });
      clone.querySelectorAll('[data-popup-next]').forEach(btn => { btn.removeAttribute('data-popup-next'); btn.setAttribute('data-pswp-360-step', '1'); });
      items.push({
        html: '<div class="pswp-slide pswp-slide--360"><div class="pswp-360" data-pswp-360>' + clone.outerHTML + '</div></div>',
        icon: badge360.innerHTML,
        kind: '360'
      });
    }
    return items;
  }

  function buildThumbs(pswpElement, pswp, items) {
    let ui = pswpElement.querySelector('.pswp__ui');
    let strip = ui.querySelector('.pswp__thumbs');
    if (!strip) {
      strip = document.createElement('div');
      strip.className = 'pswp__thumbs';
      ui.appendChild(strip);
    }
    strip.innerHTML = '';
    if (items.length < 2) {
      return;
    }
    items.forEach((item, index) => {
      let button = document.createElement('button');
      button.type = 'button';
      button.className = 'pswp__thumb pswp__thumb--' + item.kind;
      button.setAttribute('aria-label', (index + 1) + ' / ' + items.length);
      if (item.kind === '360') {
        button.innerHTML = '<span class="product-360-badge product-360-badge--thumb">' + item.icon + '</span>';
      } else {
        let img = document.createElement('img');
        img.src = item.thumb || '';
        img.alt = '';
        button.appendChild(img);
        if (item.kind === 'video') {
          let play = document.createElement('span');
          play.className = 'pswp__thumb-play';
          play.innerHTML = '<svg viewBox="0 0 10 12" aria-hidden="true"><path d="M0 0l10 6-10 6z" fill="currentColor"/></svg>';
          button.appendChild(play);
        }
      }
      button.addEventListener('click', (e) => {
        e.preventDefault();
        pswp.goTo(index);
      });
      strip.appendChild(button);
    });
  }

  // Drag to rotate, right inside the lightbox. The 360 element stops pointer events from
  // reaching PhotoSwipe, so a drag turns the bike instead of swiping to the next slide.
  function init360(root) {
    root.querySelectorAll('[data-pswp-360]:not([data-ready])').forEach((viewer) => {
      viewer.dataset.ready = '1';
      let frames = viewer.querySelectorAll('.popup-viewer-image');
      if (!frames.length) return;
      let current = Math.max(0, Array.from(frames).findIndex(img => img.classList.contains('active')));
      let startX = null;
      const sensitivity = 10;
      let show = (index) => {
        let next = ((index % frames.length) + frames.length) % frames.length;
        if (next === current) return;
        frames[current].classList.remove('active');
        frames[next].classList.add('active');
        current = next;
      };
      ['mousedown', 'touchstart', 'pointerdown'].forEach((type) => {
        viewer.addEventListener(type, (e) => e.stopPropagation());
      });
      viewer.querySelectorAll('[data-pswp-360-step]').forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          show(current + parseInt(button.getAttribute('data-pswp-360-step'), 10));
        });
      });
      viewer.addEventListener('pointerdown', (e) => {
        // Let the rotate arrows get their click; pointer capture would retarget it to the viewer
        if (e.target.closest('button')) return;
        startX = e.clientX;
        viewer.setPointerCapture(e.pointerId);
        e.preventDefault();
      });
      viewer.addEventListener('pointermove', (e) => {
        if (startX === null) return;
        let steps = Math.trunc((e.clientX - startX) / sensitivity);
        if (steps !== 0) {
          show(current + steps);
          startX += steps * sensitivity;
        }
      });
      let end = () => { startX = null; };
      viewer.addEventListener('pointerup', end);
      viewer.addEventListener('pointercancel', end);
    });
  }

  // Zoom on photos: buttons (−, fit, +) and the mouse wheel / trackpad. The photo is the original
  // upload, so zoom goes to its own resolution (1:1) — or 2x the fitted size for small uploads.
  function zoomLimits(item) {
    let min = item.initialZoomLevel;
    return { min: min, max: Math.max(min * 2, 1) };
  }

  function zoomBy(pswp, factor, point, speed) {
    let item = pswp.currItem;
    if (!item || !item.src) return;
    let limits = zoomLimits(item);
    let level = Math.min(limits.max, Math.max(limits.min, pswp.getZoomLevel() * factor));
    let center = point || { x: pswp.viewportSize.x / 2, y: pswp.viewportSize.y / 2 };
    pswp.zoomTo(level, center, speed);
  }

  function buildZoomControls(pswpElement, pswp) {
    let ui = pswpElement.querySelector('.pswp__ui');
    let controls = ui.querySelector('.pswp__zoom-controls');
    if (!controls) {
      controls = document.createElement('div');
      controls.className = 'pswp__zoom-controls';
      controls.innerHTML =
        '<button type="button" class="pswp__zoom-button" data-zoom="out" aria-label="Zoom ud"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8 12h8"/></svg></button>' +
        '<button type="button" class="pswp__zoom-button" data-zoom="fit" aria-label="Tilpas til skærmen"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg></button>' +
        '<button type="button" class="pswp__zoom-button" data-zoom="in" aria-label="Zoom ind"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8 12h8M12 8v8"/></svg></button>';
      ui.appendChild(controls);
    }
    controls.querySelectorAll('[data-zoom]').forEach((button) => {
      button.onclick = (e) => {
        e.preventDefault();
        let action = button.dataset.zoom;
        if (action === 'fit') {
          let item = pswp.currItem;
          if (item && item.src) pswp.zoomTo(item.initialZoomLevel, { x: pswp.viewportSize.x / 2, y: pswp.viewportSize.y / 2 }, 250);
        } else {
          zoomBy(pswp, action === 'in' ? 1.5 : 1 / 1.5, null, 250);
        }
        setTimeout(() => syncZoomControls(pswpElement, pswp), 280);
      };
    });
    return controls;
  }

  function syncZoomControls(pswpElement, pswp) {
    let controls = pswpElement.querySelector('.pswp__zoom-controls');
    if (!controls) return;
    let item = pswp.currItem;
    let isImage = !!(item && item.src);
    controls.hidden = !isImage;
    if (!isImage) return;
    let limits = zoomLimits(item);
    let level = pswp.getZoomLevel();
    controls.querySelector('[data-zoom="out"]').disabled = level <= limits.min + 0.001;
    controls.querySelector('[data-zoom="fit"]').disabled = level <= limits.min + 0.001;
    controls.querySelector('[data-zoom="in"]').disabled = level >= limits.max - 0.001;
  }

  function syncSlides(pswpElement, pswp) {
    init360(pswpElement);
    let current = pswp.getCurrentIndex();
    let thumbs = pswpElement.querySelectorAll('.pswp__thumb');
    thumbs.forEach((thumb, index) => thumb.classList.toggle('is-active', index === current));
    let active = thumbs[current];
    if (active) {
      // Scroll only the strip; scrollIntoView would also shift PhotoSwipe's own (overflow: hidden) layers
      let strip = active.parentElement;
      strip.scrollLeft = active.offsetLeft - (strip.clientWidth - active.clientWidth) / 2;
    }
    // Only the current slide's video may play: load it when shown, unload the others
    pswpElement.querySelectorAll('.pswp-slide__video').forEach((video) => {
      let isCurrent = pswp.currItem && pswp.currItem.container && pswp.currItem.container.contains(video);
      if (isCurrent && !video.getAttribute('src')) {
        video.setAttribute('src', video.dataset.src);
      } else if (!isCurrent && video.getAttribute('src')) {
        video.removeAttribute('src');
        if (video.tagName === 'VIDEO') video.load();
      }
    });
  }

  /**
   * @param {HTMLElement[]} slides  visible gallery slides, in order
   * @param {HTMLElement} startSlide  the slide whose zoom link was clicked
   * @param {Object} baseOptions  the theme's PhotoSwipe options
   * @param {Function} [onClose]  called with the slide shown last (null for the 360 slide)
   */
  function open(slides, startSlide, baseOptions, onClose) {
    let pswpElement = document.querySelector('.pswp');
    if (!pswpElement || typeof PhotoSwipe === 'undefined') {
      return;
    }
    let items = buildItems(slides);
    let startIndex = items.findIndex(item => item.slide === startSlide);
    let hasThumbs = items.length > 1;
    let options = Object.assign({}, baseOptions, {
      index: startIndex > -1 ? startIndex : 0,
      // Taps on our own buttons (thumbnails, 360) must not be swallowed as swipes
      isClickableElement: (el) => el.tagName === 'A' || !!el.closest('button, .pswp__thumbs, .pswp__zoom-controls'),
      getThumbBoundsFn: (i) => {
        let media = items[i] && items[i].slide ? items[i].slide.querySelector('.product-single__media') : null;
        if (!media) return undefined;
        let rect = media.getBoundingClientRect();
        return {
          x: rect.left,
          y: rect.top + (window.pageYOffset || document.documentElement.scrollTop),
          w: rect.width
        };
      }
    });

    let pswp = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
    pswp.listen('firstUpdate', () => {
      pswp.listen('parseVerticalMargin', (item) => {
        item.vGap = {
          top: 70,
          bottom: hasThumbs ? (window.innerWidth >= 768 ? 160 : 96) : 50
        };
      });
    });
    pswp.listen('afterChange', () => {
      syncSlides(pswpElement, pswp);
      syncZoomControls(pswpElement, pswp);
    });
    pswp.listen('zoomGestureEnded', () => syncZoomControls(pswpElement, pswp));
    pswp.listen('imageLoadComplete', () => syncZoomControls(pswpElement, pswp));

    // Wheel / trackpad zooms photos around the pointer. Captured on .pswp so PhotoSwipe's own
    // wheel handler (which pans a zoomed image) doesn't also run.
    let onWheel = (e) => {
      let item = pswp.currItem;
      if (!item || !item.src || e.target.closest('.pswp__thumbs')) return;
      e.preventDefault();
      e.stopPropagation();
      let delta = e.deltaMode === 1 ? e.deltaY * 40 : e.deltaY;
      zoomBy(pswp, Math.exp(-delta * 0.002), { x: e.clientX, y: e.clientY });
      clearTimeout(onWheel.timer);
      onWheel.timer = setTimeout(() => syncZoomControls(pswpElement, pswp), 80);
    };
    pswpElement.addEventListener('wheel', onWheel, { capture: true, passive: false });

    pswp.listen('close', () => {
      if (onClose) {
        let item = pswp.currItem;
        onClose(item && item.slide ? item.slide : null);
      }
    });
    pswp.listen('destroy', () => {
      pswpElement.removeEventListener('wheel', onWheel, { capture: true });
      let zoomControls = pswpElement.querySelector('.pswp__zoom-controls');
      if (zoomControls) zoomControls.remove();
      pswpElement.querySelectorAll('.pswp-slide__video').forEach(video => video.removeAttribute('src'));
      let strip = pswpElement.querySelector('.pswp__thumbs');
      if (strip) strip.innerHTML = '';
    });

    pswp.init();
    buildThumbs(pswpElement, pswp, items);
    buildZoomControls(pswpElement, pswp);
    syncSlides(pswpElement, pswp);
    syncZoomControls(pswpElement, pswp);
  }

  window.ProductLightbox = { open: open };
})();
